'use client'

import { useDevice } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import AccreditedIcon from '@public/icons/icon-accredited.png'
import GranularIcon from '@public/icons/icon-granular.png'
import BiofuelIcon from '@public/icons/lune-icon-biofuel.svg'
import HubIcon from '@public/icons/lune-icon-hub.svg'
import LiveChartIcon from '@public/icons/lune-icon-live-chart.svg'
import RouteIcon from '@public/icons/lune-icon-route.svg'
import BalluffLogo from '@public/images/logos/balluff-logo.png'
import InsettingCertificate from '@public/images/product/lune-carbon-insetting-section.png'
import CustomReports from '@public/images/product/lune-custom-reports.png'
import EmissionsIntelligence from '@public/images/product/lune-emissions-intelligence.png'
import ProviderEngagement from '@public/images/product/lune-provider-engagement.png'
import GroupView from '@public/images/solutions/lune-group-view.png'
import Image from 'next/image'

import CustomerStoryWithTiles from '@/components/CustomerStoryWithTiles'
import HomeHero from '@/components/Hero/HomeHero'
import LearnMoreTiles from '@/components/LearnMoreTiles'
import RequestDemo from '@/components/RequestDemo'
import RequestsShowcase, { type RequestsShowcaseItem } from '@/components/RequestsShowcase'
import TwoColumnsWithSquareImage from '@/components/TwoColumnsWithSquareImage'
import AsSeenInLogos from '@/sections/AsSeenInLogos'
import CTATilesSection from '@/sections/CTATilesSection'
import FortoCaseStudyBox from '@/sections/FortoCaseStudyBox'
import HomePageBlogPosts from '@/sections/HomePageBlogPosts'
import HomepageLogos from '@/sections/HomepageLogos'

const platformSteps: RequestsShowcaseItem[] = [
    {
        request: 'Collect shipment data from every carrier and forwarder',
        image: {
            src: ProviderEngagement,
            alt: "Lune's Logistics Provider Engagement screen listing logistics providers by mode, with Completed or Awaiting data status and last upload date.",
        },
        product: {
            name: 'Shipment data collection',
            icon: HubIcon,
            href: '/shipment-data-collection',
        },
        description:
            'Lune engages your suppliers and validates and standardises their data to GLEC.',
    },
    {
        request: 'Calculate emissions for every shipment and leg',
        image: {
            src: EmissionsIntelligence,
            alt: 'A shipment record with missing fields next to the version enriched by Lune: vessel identified, origin snapped to the Port of Shanghai, AIS-tracked distance, an on-carriage truck leg added, and truck type, fuel, traffic and gradient inferred.',
        },
        product: {
            name: 'Emissions calculations',
            icon: RouteIcon,
            href: '/emissions-calculations',
        },
        description:
            'Granular calculations, certified GLEC and ISO 14083 compliant, with a step-by-step explanation for every figure.',
    },
    {
        request: 'Report with confidence, from business unit to group',
        image: {
            src: CustomReports,
            alt: 'Lune report builder with a plain-language request for main-carriage well-to-wheel emissions by mode and the resulting bar chart, with Export and Schedule export buttons.',
        },
        product: {
            name: 'Analytics & reporting',
            icon: LiveChartIcon,
            href: '/analytics-reporting',
        },
        description:
            'Live KPIs, hotspots and custom reports, every number traceable to its source.',
    },
    {
        request: 'Act on your emissions',
        image: {
            src: InsettingCertificate,
            alt: 'Example Lune insetting certificate for road freight: 1 tCO₂e reduced well-to-wheel through a switch to HVO made from used cooking oil, ISCC certified and registered with 123Carbon, available for ocean, air, road and rail freight.',
        },
        product: {
            name: 'Carbon projects',
            icon: BiofuelIcon,
            href: '/carbon-projects',
        },
        description:
            'Biofuel insetting for sea, air, road and rail, and high-quality offsetting for what remains.',
    },
]

export default function Home() {
    const { mdScreen } = useDevice()

    return (
        <>
            <Box>
                <HomeHero />
                <HomepageLogos caption="Trusted by 100+ companies" />
            </Box>
            <CTATilesSection />
            <RequestsShowcase
                heading="One platform, from data to decarbonisation"
                items={platformSteps}
                label="Platform steps"
                quoted={false}
                centerHeading
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: mdScreen ? 6 : 9,
                }}
            >
                <Text variant={mdScreen ? 'h5' : 'h4'} sx={{ textAlign: 'center' }}>
                    Why companies choose Lune
                </Text>
                <LearnMoreTiles
                    tiles={[
                        {
                            icon: AccreditedIcon,
                            title: 'Figures you can defend',
                            description:
                                'Certified GLEC and ISO 14083 compliant, with a step-by-step explanation and data quality score behind every number.',
                        },
                        {
                            icon: GranularIcon,
                            title: 'Granularity that drives reductions',
                            description:
                                'Vessel-specific modelling, AIS-tracked routes and aircraft identified by flight number reveal real differences between carriers and modes. Industry averages make every option look the same.',
                        },
                        {
                            icon: RouteIcon,
                            title: 'More from the data you have',
                            description:
                                'Emissions intelligence finds the vessel, the actual route and missing truck legs, without asking anyone for more data.',
                        },
                        {
                            icon: HubIcon,
                            title: 'One platform, end to end',
                            description:
                                'Data collection, calculations, reporting, insetting and offsetting in one place, through the dashboard, API or CargoWise.',
                        },
                    ]}
                />
            </Box>
            <FortoCaseStudyBox quoted />
            <CustomerStoryWithTiles
                light={false}
                textVariant="heading"
                label="CUSTOMER QUOTE"
                logo={<Image src={BalluffLogo} width={171} height={22} alt="Balluff logo" />}
                text={
                    <>
                        &quot;Gryn by Lune has been a game changer.&quot;
                        <Text
                            component="p"
                            variant={mdScreen ? 'body2' : 'body1'}
                            sx={{ mt: mdScreen ? 3 : 4 }}
                        >
                            Daniel, Global Logistics Excellence, Balluff
                        </Text>
                    </>
                }
                rightSide={
                    <Image
                        src={GroupView}
                        alt="Lune group view: Acme Group's 7,843 tCO₂e consolidated from four business units, each fed by carriers, TMS, ERP or spreadsheets."
                        style={{ width: '100%', height: 'auto' }}
                    />
                }
            />
            <TwoColumnsWithSquareImage
                imageOnRight
                title="Built on the standards your auditors use"
                paragraph="Our methodology is certified by Smart Freight Centre as GLEC Framework and ISO 14083 compliant, and the results feed straight into GHG Protocol Scope 3 reporting."
                image={{
                    src: '/images/logo-grids/logos-ghg-iso-glec.png',
                    alt: 'GHG Protocol, ISO 14083 and GLEC Framework logos.',
                }}
            />
            <AsSeenInLogos />
            <HomePageBlogPosts />
            <RequestDemo text="See your logistics emissions in one place." />
        </>
    )
}
